import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Instagram } from "lucide-react";
import { IMAGES } from "@/lib/oneyork";

const INSTAGRAM_STUDIO_URL =
  "https://www.instagram.com/oneyorkstudio?igsh=MTk5MnNpM3F0YXRyMQ==";

export default function Gallery() {
  const railRef = useRef(null);
  const itemRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Detect which card is centered in the viewport using IntersectionObserver
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry whose intersection ratio is highest
        let best = null;
        entries.forEach((entry) => {
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry;
          }
        });
        if (best && best.isIntersecting) {
          const idx = Number(best.target.getAttribute("data-index"));
          setActiveIndex(idx);
        }
      },
      {
        root: rail,
        // Narrow center window so only the truly-centered card flips active
        rootMargin: "0px -45% 0px -45%",
        threshold: [0, 0.5, 1],
      }
    );

    itemRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollByOne = (direction) => {
    const rail = railRef.current;
    if (!rail) return;
    const firstItem = itemRefs.current[0];
    const gap = 24; // matches sm:gap-6 (1.5rem)
    const step = (firstItem?.offsetWidth || rail.clientWidth * 0.4) + gap;
    rail.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-24 lg:py-36 bg-[#0a0a0a] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-10 lg:mb-14">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-xl">
            <span className="text-xs uppercase tracking-[0.28em] text-[#d4af37] font-medium">
              Gallery
            </span>
            <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              <span className="sr-only">Recent Detailing &amp; Ceramic Coating </span>
              Recent <span className="italic font-medium">work.</span>
            </h2>
          </div>
          <p className="text-sm text-white/50 max-w-sm">
            Swipe through recent vehicles detailed and coated by the ONE YORK
            team across York Region.
          </p>
        </div>
      </div>

      {/* Horizontal swipeable rail with peek on both sides + center scale */}
      <div className="relative">
        <motion.div
          ref={railRef}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative w-full overflow-x-auto snap-x snap-mandatory no-scrollbar py-12 lg:py-20"
        >
          <div className="flex gap-4 sm:gap-6">
            {/* Leading spacer — lets the first card center */}
            <div
              aria-hidden
              className="shrink-0 w-[12vw] sm:w-[10vw] lg:w-[39vw] xl:w-[40vw]"
            />
            {IMAGES.gallery.map((item, i) => {
              const isActive = i === activeIndex;
              return (
                <div
                  key={item.src}
                  ref={(el) => (itemRefs.current[i] = el)}
                  data-index={i}
                  data-testid={`gallery-item-${i}`}
                  className={`relative snap-center shrink-0 w-[68vw] sm:w-[52vw] lg:w-[22vw] xl:w-[20vw] aspect-[4/5] overflow-hidden rounded-3xl bg-[#050505] transition-[transform,opacity,box-shadow] duration-500 ease-out will-change-transform ${
                    isActive
                      ? "lg:scale-[1.3] lg:opacity-100 lg:z-20 lg:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85),0_0_0_1px_rgba(212,175,55,0.18)]"
                      : "lg:scale-95 lg:opacity-55 lg:z-0"
                  }`}
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              );
            })}
            {/* Trailing spacer — lets the last card snap to center */}
            <div
              aria-hidden
              className="shrink-0 w-[12vw] sm:w-[10vw] lg:w-[39vw] xl:w-[40vw]"
            />
          </div>
        </motion.div>

        {/* Desktop nav buttons - bottom centered */}
        <div className="hidden lg:flex justify-center items-center gap-4 mt-8">
          <button
            type="button"
            aria-label="Previous"
            data-testid="gallery-prev-btn"
            onClick={() => scrollByOne(-1)}
            className="group flex items-center justify-center w-14 h-14 rounded-full bg-white text-black border border-white shadow-[0_12px_30px_-8px_rgba(0,0,0,0.6)] hover:bg-[#d4af37] hover:border-[#d4af37] hover:scale-105 active:scale-95 transition-all duration-300"
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={2.5} />
          </button>
          <span
            data-testid="gallery-counter"
            className="text-xs uppercase tracking-[0.3em] text-white/60 font-medium tabular-nums min-w-[3.5rem] text-center"
          >
            {String(activeIndex + 1).padStart(2, "0")} /{" "}
            {String(IMAGES.gallery.length).padStart(2, "0")}
          </span>
          <button
            type="button"
            aria-label="Next"
            data-testid="gallery-next-btn"
            onClick={() => scrollByOne(1)}
            className="group flex items-center justify-center w-14 h-14 rounded-full bg-white text-black border border-white shadow-[0_12px_30px_-8px_rgba(0,0,0,0.6)] hover:bg-[#d4af37] hover:border-[#d4af37] hover:scale-105 active:scale-95 transition-all duration-300"
          >
            <ChevronRight className="w-6 h-6" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <p className="mt-6 text-center text-[10px] uppercase tracking-[0.3em] text-white/35 lg:hidden">
        ← swipe to explore →
      </p>

      {/* Instagram studio CTA */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="mt-10 lg:mt-14 flex justify-center px-5"
      >
        <a
          data-testid="instagram-studio-cta"
          href={INSTAGRAM_STUDIO_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-2.5 bg-[#d4af37] text-black font-semibold text-sm px-7 py-3.5 rounded-full border border-[#d4af37] shadow-[0_12px_30px_-8px_rgba(212,175,55,0.45)] hover:bg-[#f2c94c] hover:border-[#f2c94c] hover:scale-[1.03] active:scale-[0.98] transition-all duration-300"
        >
          <Instagram className="w-4 h-4" strokeWidth={2.25} />
          <span>Check ONE YORK Studio Gallery</span>
        </a>
      </motion.div>
    </section>
  );
}
